import React, { useCallback, useState } from 'react';

import { supabase, useAuth } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useToast } from 'hooks/useToast';

import { prepareImageForUpload } from 'libs/imageHelpers';

import { UploadImage } from 'components/ui/UploadImage';
import { Button } from 'components/ui/Button';

import { Fonts, spacing } from 'styles/theme';
import {
    Bumper,
    CardBottomButtonWrapper,
    HorizontalFlexWrapper,
} from 'styles/shared';

const { REACT_APP_SUPABASE_URL } = process.env;

interface MultiImageUploadProps {
    onClose: () => void;
}

interface SelectedImage {
    id: string;
    file: File;
    previewUrl: string;
}

export const MultiImageUpload = ({ onClose }: MultiImageUploadProps) => {
    const { userId } = useAuth();
    const { getRetros } = useMemberGroupData();

    const { showSuccessToast, showErrorToast } = useToast();

    const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const onSaveRetroImage = useCallback(
        async (file: File) => {
            try {
                const { data: sessionData } = await supabase.auth.getSession();
                const token = sessionData.session?.access_token;
                if (!token) {
                    showErrorToast('Please sign in first.');
                    return null;
                }

                const safeFile = await prepareImageForUpload(file);
                const form = new FormData();
                form.append('file', safeFile);
                form.append('category', 'retro');

                const res = await fetch(
                    `${REACT_APP_SUPABASE_URL}/functions/v1/upload-image`,
                    {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                        body: form,
                    }
                );

                const json = await res.json();
                if (!res.ok) {
                    console.error(json);
                    showErrorToast(json.error || 'Upload failed');
                    return null;
                }
                return json as {
                    publicUrl: string;
                    objectName: string;
                };
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (e: any) {
                showErrorToast(
                    e?.message ?? 'Unexpected error uploading image'
                );
                return null;
            }
        },
        [showErrorToast]
    );

    const onSelectImage = useCallback((file: File) => {
        const previewUrl = URL.createObjectURL(file);
        const newImage: SelectedImage = {
            id: Math.random().toString(36).substr(2, 9),
            file,
            previewUrl,
        };
        setSelectedImages((prev) => [...prev, newImage]);
    }, []);

    const onRemoveImage = useCallback((imageId: string) => {
        setSelectedImages((prev) => {
            const imageToRemove = prev.find((img) => img.id === imageId);
            if (imageToRemove) {
                URL.revokeObjectURL(imageToRemove.previewUrl);
            }
            return prev.filter((img) => img.id !== imageId);
        });
    }, []);

    const onSaveRetroImages = useCallback(async () => {
        if (selectedImages.length === 0) {
            showErrorToast('Please select at least one image');
            return;
        }

        setIsUploading(true);

        try {
            // Upload all images
            const uploadPromises = selectedImages.map((image) =>
                onSaveRetroImage(image.file)
            );

            const uploadResults = await Promise.all(uploadPromises);
            const imageUrls = uploadResults
                .filter((result) => result?.objectName != null)
                .map((result) => result?.objectName as string);

            if (imageUrls.length === 0) {
                showErrorToast('Failed to upload any images');
                return;
            }

            // Get current period
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;
            const currentQuarter = Math.ceil(currentMonth / 3);
            const currentYear = currentDate.getFullYear();
            const currentPeriod = `${currentYear} Q${currentQuarter}`;

            // Save to retros table
            const { error } = await supabase
                .from('retros')
                .insert({
                    created_by: userId ?? '',
                    period: currentPeriod,
                    image_urls: imageUrls,
                })
                .select();

            if (error) {
                showErrorToast(error.message);
                return;
            }

            getRetros?.();
            showSuccessToast('Your Grand Retro images have been saved!');
            onClose();
        } catch (error) {
            showErrorToast('Failed to save Retro images');
        } finally {
            setIsUploading(false);
        }
    }, [
        selectedImages,
        showErrorToast,
        userId,
        getRetros,
        showSuccessToast,
        onClose,
        onSaveRetroImage,
    ]);

    return (
        <>
            <Fonts.Small>
                {`If you have taken The Grand Retrospective in person, or used a notebook during session, this is the space to upload images of your materials for future reference.`}
            </Fonts.Small>
            <Bumper $minHeight={spacing.xLarge} />

            {/* Upload new image */}
            <HorizontalFlexWrapper $isContentSpaced>
                <UploadImage
                    category="retro"
                    onSelectImage={onSelectImage}
                    disabled={isUploading}
                />
            </HorizontalFlexWrapper>

            {/* Show selected images */}
            {selectedImages.length > 0 && (
                <>
                    <Bumper $minHeight={spacing.xLarge} />
                    <Fonts.Medium
                        $isOpenSansSemiBold
                    >{`Selected Images:`}</Fonts.Medium>
                    <Bumper $minHeight={spacing.xSmall} />
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}
                    >
                        {selectedImages.map((image) => (
                            <div
                                key={image.id}
                                style={{
                                    position: 'relative',
                                    border: '2px solid #ddd',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                }}
                            >
                                <img
                                    src={image.previewUrl}
                                    alt="Selected retro upload"
                                    style={{
                                        width: '120px',
                                        height: '120px',
                                        objectFit: 'cover',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => onRemoveImage(image.id)}
                                    style={{
                                        position: 'absolute',
                                        top: '4px',
                                        right: '4px',
                                        background: 'rgba(0, 0, 0, 0.7)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '14px',
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <Bumper $minHeight={spacing.xxLarge} />

            <CardBottomButtonWrapper $backgroundColor="transparent">
                <Button
                    onClick={onSaveRetroImages}
                    $isDisabled={isUploading || selectedImages.length === 0}
                >
                    {isUploading ? 'Saving...' : 'Save Retro Images'}
                </Button>
            </CardBottomButtonWrapper>
        </>
    );
};
